/**
 * App root — composes all layers.
 *
 * Architecture:
 *   data/     -> raw data + cover mapping (static, no state)
 *   harness/  -> ContentService (pure logic) + useContentStore (state)
 *   components/ -> view layer (dumb components, props only)
 *
 * App wires them together. No business logic in App.
 */
import Header from './components/Header.jsx';
import Sidebar from './components/Sidebar.jsx';
import PostCard from './components/PostCard.jsx';
import PostDetail from './components/PostDetail.jsx';
import ContentGrid from './components/ContentGrid.jsx';
import ManualBanner from './components/ManualBanner.jsx';
import FavoriteBar from './components/FavoriteBar.jsx';
// T-Cron-2026-10-24 02:00 早间:改进清单 #8「长按多选 + 批量复制/收藏」。
import BatchActionBar from './components/BatchActionBar.jsx';
import MobileBottomNav from './components/MobileBottomNav.jsx';
import MobileFilterDrawer from './components/MobileFilterDrawer.jsx';
import FloatingActionButton from './components/FloatingActionButton.jsx';
import Skeleton from './components/Skeleton.jsx';
// T-Cron-2026-10-22 14:00:全局快捷键(/ 搜索、1/2 切视图、c 复制、f 收藏、? 帮助)
import useKeyboardShortcuts from './harness/useKeyboardShortcuts.js';
// T-Cron-2026-10-24 02:00 早间:共享复制语义 + 文案组装 + 触感反馈。
import { copyToClipboard } from './harness/clipboard.js';
import { buildBatchCopyText } from './harness/postText.js';
import { buzz } from './harness/haptics.js';
// T-Cron-2026-10-31 08:00:导出 toast 收口到 harness/DismissibleToast.jsx
// (点击 / Esc 关闭 + 键盘可达),4 个 toast 入口行为一致。
import { DismissibleToast } from './harness/DismissibleToast.jsx';
// T-Cron-2026-10-26 03:30 早间 — 改进清单 #5「下载体验」:批量下载封面。
// 必须 import(而不是用 window.CoverDownload)—— App.jsx 不经过 main.jsx
// 的 window 挂载时序保证,且 ESM import 才是单一事实来源。
import CoverDownload from './harness/coverDownload.js';
// T-Cron-2026-10-23 03:30:URL 参数契约层(改进清单 #12「PWA 快捷方式契约」)。
// manifest.json 声明的 `/?filter=today` 之前是死链 —— Vite 树里一处 URL 参数
// 处理都没有,长按 App 图标点「今天的内容」只会打开看板首页,筛选完全没变。
import useUrlParams from './harness/useUrlParams.js';
// T-Cron-2026-10-24 08:00:搜索历史(改进清单 #15「搜索:历史」)。
import { useSearchHistory } from './harness/searchHistory.js';
// T-Cron-2026-10-22 20:00:导出服务(改进清单 #14)。挂到 window.Exporter,
// FavoriteBar 直接用 window.Exporter.exportPosts 调用(与 ContentService 同约定)。
import './harness/exporter.js';

const SHORTCUT_GROUPS = [
  { keys: ['/'], desc: '聚焦搜索框' },
  { keys: ['1'], desc: '切换到网格视图' },
  { keys: ['2'], desc: '切换到列表视图' },
  { keys: ['e'], desc: '导出当前可见为 CSV' },
  { keys: ['m'], desc: '进入 / 退出多选(批量复制 / 收藏)' },
  { keys: ['c'], desc: '复制当前文案(详情打开时)' },
  { keys: ['f'], desc: '收藏 / 取消收藏(详情打开时)' },
  // T-Cron-2026-10-25 08:00 早间 — 改进清单 #4「快捷键」补齐的 4 个键。
  // 之前清单里写着 #4「✅ 完成」,但线上只有上面 8 个键 ——
  // 没有说明书 = 快捷键等于不存在,所以这 4 条必须和快捷键一起上线。
  { keys: ['t'], desc: '回到顶部(与右下角箭头等效)' },
  { keys: ['Shift', 'F'], desc: '跳到最新批次' },
  { keys: ['Shift', 'R'], desc: '重置全部筛选' },
  { keys: ['a'], desc: '多选模式下全选 / 取消全选' },
  // T-Cron-2026-10-28 03:30 (T118) — 改进清单 #2「快捷键」补齐。
  //
  // 🔴 Shift+C 早就实现了(App.jsx 已接 onCopyAllVersions,useKeyboardShortcuts
  //    里也有 'C' 分支),但**从没进过这个帮助面板** ⇒ 用户按了没反应,
  //    也不知道有这个键。快捷键没有说明书 = 不存在。
  { keys: ['Shift', 'C'], desc: '复制 A/B/C 三版(详情打开时)' },
  // 详情抽屉内的快捷键(T118 补齐:1/2/3 切版本、←/→ 切版、D 下载、S 分享)。
  // 注意:面板里 `1`/`2` 已经有「切视图」一条了 —— 那是**看板层**的语义。
  // 抽屉打开时数字键归「切版本」(详情层),两条互不冲突(详情打开时
  // 全局 handler 让位),所以这里明确写「详情打开时」避免歧义。
  { keys: ['1'], desc: '详情打开时:切到 A 版' },
  { keys: ['←', '→'], desc: '详情打开时:上一个 / 下一个版本' },
  { keys: ['d'], desc: '详情打开时:下载当前版封面' },
  { keys: ['s'], desc: '详情打开时:分享当前版封面' },
  { keys: ['?'], desc: '打开 / 关闭本帮助' },
  { keys: ['Esc'], desc: '关闭详情 / 筛选 / 本帮助' },
  // ────────────────────────────────────────────────────────────────────
  // 🔴 T137 (2026-11-03 08:00 早间) — 补齐「已实现但从没进过说明书」的快捷键。
  //
  // 【为什么必须补】上一轮(T136)的审计结论:
  //   面板里 18 条**全部**与实现一致(无 P0/P1 错配)—— 但实现侧还有
  //   **11 组**快捷键从来没进过这个面板。
  //   ⇒ 用户按了没反应(以为是坏的),或者压根不知道有这个键。
  //   「快捷键没有说明书 = 不存在」—— 这个项目从 T118 起就一直在补,
  //   这一轮把它补齐到「实现 ⊆ 说明书」。
  //
  // 【口径】每条都写明**生效上下文**(看板层 / 详情层 / 卡片聚焦 / 抽屉内),
  //   否则同一个物理键在两层含义不同 → 用户按了「没反应」还是「反应错了」,
  //   都说不清是哪个上下文的问题。
  // ────────────────────────────────────────────────────────────────────

  // 详情抽屉内的版本切换(T136 审计 M2/M3:实现是 1~9,面板只写了 1)。
  // 实现:PostDetail.jsx 按 Object.keys(post.versions).sort() 的第 N 个。
  // 「A/B/C」是当前数据集的版本键,实现本身是**按序**而非按字母。
  { keys: ['2'], desc: '详情打开时:切到 B 版(1=A / 2=B / 3=C)' },
  { keys: ['3'], desc: '详情打开时:切到 C 版' },

  // 卡片层(PostCard.jsx:263)—— 卡片可聚焦(tabIndex=0),聚焦后这些键归卡片。
  { keys: ['Enter'], desc: '卡片聚焦时:打开详情' },
  { keys: ['←', '→'], desc: '卡片聚焦时:上一个 / 下一个版本' },
  { keys: ['1'], desc: '卡片聚焦时:直选第 N 个版本' },

  // 侧栏重排(Sidebar.jsx:138)—— 已通过 aria-keyshortcuts 对读屏公开,
  // 但面板从没有它 ⇒ 读屏用户「知道」但视觉用户完全不知道。
  { keys: ['Alt', '↑'], desc: '侧栏聚焦时:上移当前分组(配合 ↓ 连续重排)' },
  { keys: ['Alt', '↓'], desc: '侧栏聚焦时:下移当前分组' },

  // 各弹层的 Esc 语义 —— 之前用一条「Esc 关闭详情/筛选/本帮助」概括,
  // 实际有 4 个互斥的 Esc 语义(帮助面板 / 详情抽屉 / 筛选抽屉 / 导出面板)。
  { keys: ['Esc'], desc: '关闭当前弹层(导出面板 / 保存封面面板同样适用)' },

  // 移动端筛选抽屉内(MobileFilterDrawer.jsx:144)。
  { keys: ['Enter'], desc: '筛选抽屉内:提交搜索并记入历史' },

  // 导出菜单内(Header.jsx:97)—— 菜单获得焦点后用方向键导航。
  { keys: ['↑', '↓'], desc: '导出菜单内:上下选择格式' },
];

// ────────────────────────────────────────────────────────────────────────
// 🔴 T138 (2026-11-03 03:30 早间) — 移动端「触屏操作清单」。
//
// 【缺陷(实测)】T135 给快捷键面板补了 ⌨ 可见入口 ⇒ 移动端**能打开**了。
//   但面板里 28 条全是**物理键位说明**(kbd 标签:1 / 2 / e / m / ← → / Shift+F …)
//   实测(320 / 375 / 430 三个宽度,Playwright + iPhone UA):
//     kbdCount=36 / touchCount=0 / variant=keyboard
//   ⇒ 手机用户点开面板,看到的是一整页「你按不到的键」⇒ **净负价值**。
//   (本项目手机是主要使用场景 —— 见 T135 的 ⌨ 按钮注释。)
//
// 【为什么不能把物理键位也留着】用户是**触屏**用户,不是「暂时没接键盘」:
//   列 28 条按不到的键 = 28 条噪音 ⇒ 反而把真正能点的入口淹了。
//   ⇒ 移动端**换一套内容**,不是「追加」。
//
// 【口径】只列**真的能用手点出来**的动作 + **它在哪**(位置提示)。
//   位置提示是关键:「点右上角 ⌨」这种用户能照做的描述才有意义。
//
// 【桌面端零回归】桌面仍渲染 SHORTCUT_GROUPS(28 条物理键位)。
//   判据用 `(hover: none) and (pointer: coarse)`(与 coverDownload.js 一致),
//   **不用**视口宽度 —— 因为「窄窗口的桌面浏览器」仍然有物理键盘。
// ────────────────────────────────────────────────────────────────────────
const TOUCH_ACTIONS = [
  { icon: '⌕', name: '搜索', where: '点顶部搜索框,或点侧栏的搜索输入框' },
  { icon: '☰', name: '筛选', where: '移动端底部「筛选」标签 → 选书/日期/标签/角度' },
  { icon: '⊞', name: '切换网格 / 列表', where: '点顶部视图切换按钮' },
  /* 🔴 T141(2026-11-03 20:00 晚间轮)—— 触屏窄屏下 header 里的「导出」二字
     被 a11y.css 的 max-width:720px 规则 display:none 掉了
     ⇒ 「点顶部『导出』按钮」指向一个**触屏用户看不见的标签**。
     ⇒ 改成描述**看得见的形状**:⤓ 图标 + 它右边的「格式」按钮。 */
  { icon: '⤓', name: '导出 CSV / JSON', where: '点顶部 ⤓ 图标导出 CSV,点它右边的「格式」选 JSON' },
  { icon: '☑', name: '多选批量操作', where: '点顶部「多选」→ 勾选卡片 → 批量复制 / 收藏' },
  { icon: '♡', name: '收藏 / 取消收藏', where: '点卡片上的爱心,或在详情里按收藏' },
  { icon: '⧉', name: '复制文案', where: '在详情抽屉里点「复制」' },
  { icon: '⤓', name: '批量下载封面', where: '多选模式 → 勾选 → 点「下载封面」' },
  { icon: '✕', name: '关闭弹层', where: '点遮罩 / 关闭按钮(左上角 ✕)' },
  { icon: '?', name: '这份说明', where: '点顶部 ⌨ 按钮随时回到这里' },
];

function App() {
  const store = useContentStore();

  // T-Cron-2026-10-24 08:00:搜索历史(改进清单 #15)。
  // 状态放在 App 层 ⇒ 侧栏和移动抽屉**共用同一份历史**(两个入口,
  // 一份数据,不会出现「手机上查过、桌面看不到」)。
  const searchHistory = useSearchHistory();

  const [showBackToTop, setShowBackToTop] = React.useState(false);
  const [viewMode, setViewMode] = React.useState('grid');
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [mobileTab, setMobileTab] = React.useState('home');
  const [loading, setLoading] = React.useState(true);
  const [helpOpen, setHelpOpen] = React.useState(false);
  // 🔴 T138 (2026-11-03 03:30 早间) — 导出菜单(Header 的 ▾ → CSV/JSON)是否开着。
  //
  // 【为什么需要它】导出菜单是 role="menu",**不是** aria-modal ⇒ 它不在
  //   T136/T137 建立的模态守卫里 ⇒ 菜单开着时单键会穿透到背景:
  //     · `2` → 背景被切成列表视图
  //     · `e` → **导出两次**(菜单 item 一次 + 全局 e 一次)
  //     · `m` → 背景进入多选态
  //   (实测 pre-fix:list-mode / downloads=2 / batch_bar=True。)
  //
  // 【为什么用「上报」而不是「受控」】菜单的开关有 4 个触发点,
  //   全部收口在 Header 内部的 setExportMenuOpen ⇒ Header 用 effect 上报,
  //   本组件只持有镜像状态(单向数据流,不会和 Header 打架)。
  const [exportMenuOpen, setExportMenuOpen] = React.useState(false);

  // 🔴 T138 (2026-11-03 03:30 早间) — 是不是「纯触屏」设备。
  //
  // 【判据为什么是 (hover: none) and (pointer: coarse)】与 coverDownload.js 一致。
  //   ⚠️ **不用**视口宽度 —— 窄窗口的桌面浏览器仍然有物理键盘,
  //      按宽度切换会让「桌面用户把窗口拉窄」就丢掉了快捷键说明。
  //   而 (hover:none)+(pointer:coarse) 精确命中「没有鼠标、只有手指」的设备。
  //
  // 【为什么要监听而不是只读一次】用户可能接上外接键盘 / 拔掉外接键盘
  //   (iPad 的 Magic Keyboard 就是这种)⇒ matchMedia 的 change 事件要用。
  const [isTouchOnly, setIsTouchOnly] = React.useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
    const onChange = (e) => setIsTouchOnly(e.matches);
    // Safari < 14 只支持 addListener;新版本同时支持 addEventListener。
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  // T-Cron-2026-11-01 08:00 早间 — a11y #10:快捷键帮助面板补齐模态语义。
  //
  // 🔴 这个面板**已经自称**「按 ? 或 Esc 关闭」(footer 文案),
  //   但它只声明了 role="dialog" + aria-modal="true",
  //   没有焦点陷阱、没有 tabIndex、**也没有真正处理 Escape**。
  //   ⇒ 声明了 aria-modal 却没有对应的模态行为 = 比不声明更糟:
  //     读屏告诉用户「背景不可交互」,但实际 Tab 能跑到背后的卡片上,
  //     而且用户按 Esc(文案承诺过的按键)没反应 ⇒ 用户以为键盘坏了。
  //   ⇒ 这正是「文案承诺 vs 实际行为」不一致 —— 比纯缺失更值得修。
  //
  // 收口到 harness/useFocusTrap.js,与其它弹层同源。
  const helpPanelRef = React.useRef(null);
  useFocusTrap(helpPanelRef, { enabled: helpOpen, autoFocus: true });

  // Escape 关闭帮助面板(补上文案承诺但没实现的那一半)。
  // 🔴 capture 阶段 + stopPropagation:全局快捷键 handler 也在 document 上,
  //   不拦住的话一次 Esc 会同时关面板 + 触发别的全局动作。
  React.useEffect(() => {
    if (!helpOpen) return undefined;
    const onHelpKey = (e) => {
      // 🔴 T137 (2026-11-03 08:00 早间) — 长按(auto-repeat)守卫。
      if (e.repeat && !(e.ctrlKey || e.metaKey || e.altKey)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setHelpOpen(false);
      }
    };
    document.addEventListener('keydown', onHelpKey, true);
    return () => document.removeEventListener('keydown', onHelpKey, true);
  }, [helpOpen]);

  // T-Cron-2026-10-22 14:00:搜索框 ref。桌面端在侧栏,移动端在筛选抽屉里,
  // 两处都是同一个受控输入(filters.search),所以 `/` 只需要聚焦其中可见的一个。
  const sidebarSearchRef = React.useRef(null);
  const mobileSearchRef = React.useRef(null);

  const focusSearch = React.useCallback(() => {
    const isMobile = window.matchMedia('(max-width: 600px)').matches;
    if (isMobile) {
      setFilterOpen(true);
      // 抽屉有入场动画,下一帧再聚焦,否则输入框还没挂载
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (mobileSearchRef.current) mobileSearchRef.current.focus();
        });
      });
    } else if (sidebarSearchRef.current) {
      sidebarSearchRef.current.focus();
      sidebarSearchRef.current.select();
    }
  }, []);

  // T-Cron-2026-10-22 14:00:快捷键的「复制当前文案」需要 PostDetail 内部的
  // 文案组装逻辑(title + body + tags)。PostDetail 已经把组装好的字符串
  // 暴露成 ref,这里通过 CustomEvent 让 PostDetail 执行它的复制流程 ——
  // 避免在 App 里重复实现一份文案组装(两份实现必然漂移)。
  const copyDetailText = React.useCallback(() => {
    window.dispatchEvent(new CustomEvent('xhs:copy-detail-text'));
  }, []);

  // T-Cron-2026-10-26 03:30 早间 — 快捷键 Shift+C = 复制 A/B/C 三版。
  // 与上面单键 c 同约定:App 只发事件,文案组装由 PostDetail 负责,
  // 两处不重复实现(两份实现必然漂移)。
  const copyDetailAllVersions = React.useCallback(() => {
    window.dispatchEvent(new CustomEvent('xhs:copy-detail-all-versions'));
  }, []);

  const toggleDetailFav = React.useCallback(() => {
    const id = store.detailId;
    if (!id) return;
    store.toggleFavorite(id);
  }, [store.detailId, store.toggleFavorite]);

  // T-Cron-2026-10-23 03:30 — 改进清单 #13「导出当前可见」。
  //
  // 上一轮(20:00)落地了 CSV/JSON 导出,但**只有 favorites 一个入口**
  // —— 必须先一篇一篇点收藏,才能导出。
  // 用户真正的日常场景恰恰相反:「今天这批 12 篇我都要」→ 先按日期筛出今天的
  // → 直接导出今天的 12 篇。走 favorites 路径要手点 12 次收藏,完全不可用。
  //
  // 老仓库(根目录 App.jsx:1400)有这条能力(handleExportCSV 导出 store.filtered),
  // Vite 迁移时丢了 —— 本轮补回。
  //
  // 口径:导出 **store.filtered**(已应用全部筛选的可见集合),不是全量 POSTS。
  // 这样「按日期筛出今天 → 导出」拿到的就是今天那批,不会混进历史内容。
  const [exportToast, setExportToast] = React.useState('');
  const exportToastTimer = React.useRef(null);

  const showExportToast = React.useCallback((msg) => {
    setExportToast(msg);
    if (exportToastTimer.current) clearTimeout(exportToastTimer.current);
    exportToastTimer.current = setTimeout(() => setExportToast(''), 2600);
  }, []);

  React.useEffect(() => () => {
    if (exportToastTimer.current) clearTimeout(exportToastTimer.current);
  }, []);

  const exportVisible = React.useCallback((format = 'csv') => {
    const visible = store.filtered || [];
    const result = window.Exporter.exportPosts(visible, format, 'visible');
    if (result.ok) {
      // 触感反馈:导出是"完成了一个动作",给一次短促的确认震动。
      // T-Cron-2026-10-24:收口到 harness/haptics.js(尊重 prefers-reduced-motion
      // 的判断此前在这里内联了一份,现在全项目只有一处实现)。
      buzz(12);
      showExportToast(result.message);
    } else {
      showExportToast(result.message || '导出失败,请重试');
    }
  }, [store.filtered, showExportToast]);

  // T-Cron-2026-10-23 03:30 — 改进清单 #12:URL 参数契约。
  //
  // manifest.json 声明的 4 个入口,全部在这里接到真实行为:
  //   ?filter=today     → 跳到最新一天(等价老仓库 ?action=newest)
  //   ?view=favorites   → 只看已选素材
  //   ?action=search    → 聚焦搜索框
  //   ?action=export    → 导出当前可见
  //
  // 「最新一天」的口径 = 数据集里 date 最大的那一天(与 ContentService.isToday
  // 同一判据:数据快照的 newest,而不是系统今天 —— 半夜跑批时数据集还没更新,
  // 按系统今天会筛出 0 条)。
  const jumpToToday = React.useCallback(() => {
    const all = store.posts || [];
    if (!all.length) {
      showExportToast('暂无内容');
      return;
    }
    // T-Cron-2026-11-04 (T146) — 改进清单 #3「触感反馈」。
    // 「最新一批」胶囊是 header 上最高频的可点元素之一(改进清单 #9 的入口),
    // 但它是**唯一**一个完全没有触觉确认的 header 动作 ——
    // 旁边的「导出」「多选」「刷新」都有 buzz,只有它按下去是静默的。
    // 口径:轻触感 8ms(与 PostCard / MobileBottomNav 的 tap 档位一致)。
    // buzz() 内部已处理「设备不支持 vibrate」(iOS Safari 无此 API)、
    // 「prefers-reduced-motion: reduce」与「异常吞掉」三种情况
    // ⇒ 有则震、无则静默,绝不影响筛选功能。
    buzz(8);
    const newest = all.reduce((acc, p) => (p.date > acc ? p.date : acc), all[0].date);
    store.setFilter('date', newest);
    const count = all.filter(p => p.date === newest).length;
    showExportToast(`已筛选 ${newest} · ${count} 篇`);
  }, [store.posts, store.setFilter, showExportToast]);

  const showFavoritesOnly = React.useCallback(() => {
    store.setFilter('onlyFavorites', true);
    setMobileTab('favorites');
    showExportToast('已筛选已选素材');
  }, [store.setFilter, showExportToast]);

  // ────────────────────────────────────────────────────────────────────
  // T-Cron-2026-10-24 02:00 早间 (round-1-of-day) — 改进清单 #8「批量操作」
  //
  // 长按卡片 → 进入多选态 → 批量复制 / 批量收藏。
  //
  // 状态设计:
  //   selectedIds   —— Set<string>,存 id 不存对象。存对象会在 posts 被
  //                    toggleFavorite 重建时持有过期引用(收藏态不同步)。
  //   selectionMode —— 是否处于多选态。选中数量为 0 时自动退出多选态,
  //                    否则用户会卡在一个"看不见但还在拦截点击"的模式里 ——
  //                    这比没有多选功能更糟。
  //
  // 与既有机制的关系(不重复造轮子):
  //   · 收藏走 store.setFavorites(本轮新增),复用同一条 localStorage 持久化通道;
  //   · 复制走 harness/clipboard.js 的三段式,与 PostDetail 同源;
  //   · 文案组装走 harness/postText.js,与 PostDetail 同源。
  // ────────────────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());

  // T-Cron-2026-10-24 03:30 早间 (round-2-of-day):把「是否处于多选态」
  // 和「选中了几个」拆成两个状态。
  //
  // 02:00 轮落地 #8 时,`selectionMode` 是从 `selectedIds.size > 0` 派生的 ——
  // 在「长按进入」的语义下这是对的(长按必然选中一张)。
  // 但它让**桌面端完全无法进入多选**:桌面没有 touch 事件,onTouchStart
  // 永不触发 ⇒ 这个功能在桌面 = 不可用(只有 444 张卡片的看板,
  // 桌面用户反而是主力场景之一)。
  //
  // 修法:显式开关 + 选中集合两个状态,任一为真 ⇒ 进入多选态。
  //   · 桌面:点 Header「多选」按钮(或按 M)→ 进入 → 点卡片勾选
  //   · 移动:长按卡片 → 勾选 + 自动进入(下面的 effect 兜住)
  //   · 退出:操作条「取消」/ 再点一次「多选」按钮 ⇒ 清空并退出
  const [selectMode, setSelectMode] = React.useState(false);
  const selectionMode = selectMode || selectedIds.size > 0;

  // 🔴🔴 T-Cron-2026-11-04 03:30 早间 (T143) — 「进入多选态」收口到**唯一入口**。
  //
  // 【背景:T140/T141/T142 连挂三轮的同一条待办】
  //   T140 在 `toggleSelectMode` 里加了护栏 `if (store.detailId) return;`
  //   (抽屉开着 ⇒ 不进多选态,因为 BatchActionBar 会被抽屉 100% 遮挡 ⇒
  //    所有批量按钮点不到、且用户连「取消」都点不到 ⇒ 死锁)。
  //   T142 §4 实测发现:**那条护栏只挂在 1 条路径上**。全仓有 **3 条**
  //   进入多选态的路径:
  //     ① Header「多选」按钮 / 快捷键 `m` → toggleSelectMode  ✅ 有护栏
  //     ② 触屏长按卡片 450ms          → toggleSelect       ❌ 无护栏
  //     ③ setSelectedIds 后自动开多选  → useEffect          ❌ 无护栏
  //   ⇒ T142 实测:导出菜单开着时长按会进入多选态
  //     (batchBarPresent:true, selectedCards:1, menuStillOpen:true)
  //
  // 【为什么这在当时是「加固漏洞」而不是「现网 P0」】
  //   T142 §4.3 证伪了症状可达性:抽屉 overlay(z=100,inset:0)盖住全视口
  //   ⇒ 长按落不到后面的卡片;且多选态下 `handleCardActivate` 直接 return
  //   ⇒ 点卡片永远打不开抽屉。
  //   ⇒ 但**导出菜单** z-index:60 / position:absolute ⇒ **不遮卡片**
  //     ⇒ ① 长按 → ② 多选态 + 批量条 ⇒ ③ 导出菜单还开着 ⇒ 两层浮层叠加。
  //   ⇒ 换句话说:**护栏是「按入口枚举」写的,不是「按不变量」写的** ——
  //     将来新增**任何**一个入口(读屏脚本 / 浏览器扩展 / 新按钮 / 新手势),
  //     死锁立刻复活,而且用户无法自救(「取消」也在被挡的层下面)。
  //
  // 【本轮修法:把护栏从「某个函数里的一行 if」升级为「唯一入口 + 不变量」】
  //   ① 新增 `enterSelectMode()` —— **唯一**能开多选态的函数,
  //      内含护栏;所有路径都必须经过它。
  //   ② 新增 `canEnterSelectMode()` —— 护栏本身,提出来以便复用 + 便于探针断言。
  //   ③ `toggleSelect` 改为:若当前不允许进多选态 ⇒ **整个选择动作也不做**
  //      (否则会「选中了但没进多选态」,用户看见勾选了却什么也没发生 ⇒ 更迷惑)。
  //   ④ 删掉 `useEffect([selectedIds]) → setSelectMode(true)` 这条**旁路**,
  //      改由 `enterSelectMode` 显式开 —— 副作用派生的状态迁移全部收口。
  //
  // 【护栏覆盖的不变量】
  //   · 抽屉开着(store.detailId)⇒ 不进多选态(T140 的原始意图,保留)
  //   · 导出菜单开着(exportMenuOpen)⇒ 不进多选态(T142 实测的新发现)
  //     ⇒ 导出菜单是 z:60 的浮层,长按能落到卡片上 ⇒ 两条浮层会叠在一起。
  //   · 其它模态(帮助 / 筛选)由 T136 的模态栈挡快捷键;长按路径也一并挡掉,
  //     避免「长按穿透模态」。
  //
  // 【为什么「挡掉」而不是「自动关掉模态」】
  //   与 T140 同一条理由:模态是**用户主动打开的**、承载正在看的内容;
  //   多选只是一个工具态 ⇒ 模态优先,工具态让路。
  //   (也保住了 T139 的焦点归还语义:抽屉的 trap 不会被中途打断。)

  // 护栏的**唯一真相源**:能挡住多选态的模态/浮层。
  const canEnterSelectMode = React.useCallback(() => {
    if (store.detailId) return false;      // T140:抽屉开着(遮挡批量条 ⇒ 死锁)
    if (exportMenuOpen) return false;     // T142:导出菜单 z:60,不遮卡片 ⇒ 会叠加
    if (helpOpen) return false;           // T143:长按穿透帮助面板
    if (filterOpen) return false;         // T143:长按穿透筛选抽屉
    return true;
  }, [store.detailId, exportMenuOpen, helpOpen, filterOpen]);

  // **唯一**能开多选态的函数。所有入口都必须经过它。
  const enterSelectMode = React.useCallback(() => {
    if (!canEnterSelectMode()) return false;
    setSelectMode(true);
    return true;
  }, [canEnterSelectMode]);

  const toggleSelect = React.useCallback((postId, next) => {
    // 🔴 T143:护栏前置 —— 不允许进多选态时,**连选择动作都不做**。
    //   (只挡一半 ⇒ 「勾选了却没进多选态」= 用户看到勾却无反应 ⇒ 更迷惑。)
    //   注意:已经处于多选态时**允许**继续改选择(退出多选态由 clearSelection 负责)。
    if (!selectMode && !canEnterSelectMode()) return;
    setSelectedIds(prev => {
      const nextSet = new Set(prev);
      const shouldSelect = next === undefined ? !nextSet.has(postId) : !!next;
      if (shouldSelect) nextSet.add(postId);
      else nextSet.delete(postId);
      return nextSet;
    });
    // 🔴 T143:原来是 `useEffect([selectedIds]) → setSelectMode(true)`(旁路)。
    //   现在选中即显式进入多选态,不再靠副作用派生 ⇒ 状态迁移只有一个入口。
    if (!selectMode) enterSelectMode();
  }, [selectMode, canEnterSelectMode, enterSelectMode]);

  const clearSelection = React.useCallback(() => {
    setSelectedIds(new Set());
    setSelectMode(false);
  }, []);

  // Header「多选」按钮 / 快捷键 M:显式进出多选态。
  //
  // 🔴 T-Cron-2026-11-03 14:00 午间 (T140) — 补 `isDetailOpen` 状态互斥护栏。
  //
  // 【背景:T136 §1 记的 P0 —— 「详情抽屉打开时进入多选态 ⇒ BatchActionBar
  //   被抽屉 100% 遮挡,所有批量按钮点不到」。T136/T137/T138/T139 四轮都没碰。
  //
  // 【本轮先证伪了它的「用户可达性」(运行时探针实测)】
  //   ① `openDetail` 全仓**只有一个**调用点(PostCard.onClick),而
  //      `handleCardActivate` 在 `selectionMode` 为真时**直接 return**
  //      ⇒ 多选态下点卡片**永远打不开抽屉**。
  //   ② 抽屉开着时按 `m` ⇒ 被 T136 的模态守卫挡住(抽屉分支只放行 ?/Esc/c/C/f)。
  //   ③ 抽屉开着时点 Header「多选」按钮 ⇒ 被 .drawer-overlay(z=100,inset:0)
  //      / .drawer 本身挡住 ⇒ 点不到。
  //   ⇒ **现状不可达** —— T136 的 P0 在它自己修完模态守卫那天就已经关闭了
  //     (T136 文档的 Option C 就是那个补丁,同一份文档自证)。
  //
  // 【但护栏本身确实缺】探针实测:用 JS 绕过遮挡触发「多选」按钮,
  //   ⇒ batchBar=True 且抽屉仍开着 ⇒ 批量条**确实**被挡住
  //     (桌面 hit=overlay / 移动 hit=drawer 本体)。
  //   ⇒ `toggleSelectMode` 里**没有**任何 isDetailOpen 判断 ⇒
  //     只要将来新增**任何**一个进入多选态的入口(读屏脚本 / 扩展 /
  //     新按钮 / 快捷键),这个遮挡立刻复活,而且用户**无法退出多选态**
  //     (批量条上的「取消」按钮也在抽屉底下)。
  //
  // 【为什么不是「调 z-index」】T136 已经写明:把 .batch-bar 的 z-index 提到
  //   >101 会让页面级工具条压在模态之上,语义更乱。**首选状态互斥。**
  //
  // 【为什么「进多选时关抽屉」而不是「开抽屉时退多选」】
  //   · 抽屉是**用户主动打开**的,承载的是正在看的内容(信息价值高)。
  //   · 多选只是一个工具态。
  //   ⇒ 抽屉优先:抽屉开着就不让进多选态,而不是把抽屉踢掉。
  //   (也保住了 T139 的焦点归还语义:抽屉的 trap 不会被中途打断。)
  const toggleSelectMode = React.useCallback(() => {
    // 🔴 T143:护栏已上移到 `canEnterSelectMode`(唯一真相源)。
    //   抽屉 / 导出菜单 / 帮助 / 筛选 任一开着 ⇒ 都不进多选态。
    //   ⇒ 多选态与所有浮层**互斥**,不再只挡抽屉这一条路径。
    if (selectMode) {
      setSelectMode(false);
      setSelectedIds(new Set());
    } else {
      enterSelectMode();
    }
  }, [selectMode, enterSelectMode]);

  // 筛选变化后,原本选中的卡片可能已经不在可见集合里 —— 保留选择
  // (用户可能只是切了视图),但复制/收藏时按**当前可见集合**取对象,
  // 避免操作到已经被筛掉的条目。
  //
  // 🔴 T-Cron-2026-11-01 14:00 — 修「计数口径与执行口径脱节」(P1)。
  //
  // 【原缺陷(实测复现)】
  //   操作条上写「已选 N 篇」,N = selectedIds.size(全量选中数);
  //   但执行口径 selectedPosts = selectedIds ∩ store.filtered(可见交集)。
  //   ⇒ 用户选 3 篇 → 点侧栏筛一个筛不到这 3 篇的书籍 →
  //     操作条仍显示「已选 3 篇」,四个动作按钮全部 enabled,
  //     点「复制」却弹「没有可复制的已选内容」⇒ **点了没反应**。
  //   ⇒ 同一屏里「全选」按钮因 visibleCount===0 被 disabled,
  //     旁边四个动作按钮却 enabled ⇒ 这一屏的状态本身自相矛盾。
  //
  // 【为什么不改成「筛选变化就清空选中」】
  //   那样会**静默销毁用户的选材成果**:用户选中 8 篇,想看看 A 版长什么样,
  //   随手切了个筛选,回来发现 8 篇全没了 ⇒ 比现在的错更严重。
  //   ⇒ 保留选中,但让 UI **如实说明**当前可操作的范围。
  //
  // 【修法:两个数字都摆出来】
  //   · count       = 实际可操作数(selectedPosts.length)—— 按钮口径,
  //                  与 aria-label / 复制 toast 里的数字**完全一致**;
  //   · hiddenCount = 被筛掉的数量 —— 显式提示「N 篇在当前筛选外」。
  //   ⇒ selectedPosts.length === 0 时,动作按钮一律 disabled,
  //     用户看到的是「按钮不可点 + 一句解释」,而不是「可点但没反应」。
  const selectedPosts = React.useMemo(() => {
    if (!selectedIds.size) return [];
    const visible = new Set((store.filtered || []).map(p => p.id));
    return (store.posts || []).filter(p => selectedIds.has(p.id) && visible.has(p.id));
  }, [selectedIds, store.filtered, store.posts]);

  // 🔴 T-Cron-2026-11-03 08:00 早间 (T139) — 修「批量复制输出顺序 ≠ 用户点选顺序」。
  //
  // 【缺陷(T138 §「下轮建议」第 2 条)—— 本轮已用运行时探针实测复现】
  //   selectedPosts 走的是 `(store.posts || []).filter(...)`
  //   ⇒ 输出顺序 = **数据集序**(POSTS 常量数组序)。
  //   而用户在屏幕上的浏览序是 **DOM 序**(groupByDate 后的日期倒序),
  //   用户的**点选序**是 selectedIds(Set)的插入序。
  //   实测三种序**互不相同**:
  //     DOM 序  = ['鸿蒙Next来了', '空间站第六次出舱', '长安诡事录开机后']
  //     点选序  = ['鸿蒙Next来了', '长安诡事录开机后', '空间站第六次出舱']  (点 0→2→1)
  //     数据集序 = ['长安诡事录开机后', '空间站第六次出舱', '鸿蒙Next来了']  ← 剪贴板实测输出
  //   ⇒ 用户点 3 张、复制出来的是另一个顺序 ⇒ 粘到小红书后顺序和自己的
  //     浏览顺序对不上,还得手动重排(10 篇就是 10 段重排)。
  //
  // 【为什么只改「复制」,不改 selectedPosts】
  //   selectedPosts 还有 4 个下游消费者:计数 / 批量收藏 / 批量取消收藏 /
  //   批量下载封面 —— 这些都是**顺序无关**的集合语义
  //   (收藏是「把这些设成已收藏」,下载是「逐张保存」)⇒ 改它们的顺序没有收益,
  //   反而会让「全选后批量收藏」这类操作变得不可预测。
  //   ⇒ 只为「复制」单独开一条**点选序**通道,其余口径一律不动。
  //
  // 【为什么点选序 = Set 的插入序是对的,不用另建一个 pickedAt 数组】
  //   · Set 的迭代序就是插入序(JS 规范保证)⇒ selectedIds 本身就是
  //     「用户点选的先后顺序」的现成记录,不需要新增状态。
  //   · 「全选」时 toggleSelectAll 按 `store.filtered` 的序插入
  //     ⇒ 全选后的点选序 == DOM 序 == 用户看到的顺序 ⇒ 同样正确。
  //   · 「全选后又手动加选几张」⇒ Set 序 = 先全选的那批(可见序)+ 后加的
  //     ⇒ 语义自然(用户先看到的在前),无需特殊处理。
  //
  // 【可见性过滤仍然要做】
  //   Set 里可能有被当前筛选排除掉的 id(hiddenSelectedCount > 0)
  //   ⇒ 必须在点选序上再过一道可见性过滤,否则复制出来会带上
  //     「界面上根本看不到」的卡片(计数口径也会对不上)。
  const selectedPostsInPickOrder = React.useMemo(() => {
    if (!selectedIds.size) return [];
    const byId = new Map((store.posts || []).map(p => [p.id, p]));
    const visible = new Set((store.filtered || []).map(p => p.id));
    const out = [];
    // for...of 遍历 Set ⇒ 插入序(点选序)
    for (const id of selectedIds) {
      if (!visible.has(id)) continue;      // 被筛掉的不可操作
      const post = byId.get(id);
      if (post) out.push(post);
    }
    return out;
  }, [selectedIds, store.filtered, store.posts]);

  // 被当前筛选排除掉的选中数(总数 - 可操作数)。
  const hiddenSelectedCount = Math.max(0, selectedIds.size - selectedPosts.length);

  const batchToastTimer = React.useRef(null);
  const showBatchToast = React.useCallback((msg) => {
    showExportToast(msg);
    if (batchToastTimer.current) clearTimeout(batchToastTimer.current);
    batchToastTimer.current = setTimeout(() => { batchToastTimer.current = null; }, 2600);
  }, [showExportToast]);

  React.useEffect(() => () => {
    if (batchToastTimer.current) clearTimeout(batchToastTimer.current);
  }, []);

  const batchCopy = React.useCallback(async () => {
    // 🔴 T-Cron-2026-11-03 08:00 (T139):用**点选序**而不是数据集序
    //   (selectedPostsInPickOrder 的定义见上方注释)。
    //   ⇒ 复制出来的顺序 = 用户点选的顺序 = 眼睛看到的顺序。
    //   ⇒ 内容集合与 selectedPosts 完全一致(同一个 visible 过滤口径)
    //     ⇒ 计数 / toast 里的「N 篇」与实际复制出来的条数永远相等。
    const list = selectedPostsInPickOrder;
    if (!list.length) {
      showBatchToast('没有可复制的已选内容');
      return;
    }
    const text = buildBatchCopyText(list, store.filters.version);
    if (!text) {
      showBatchToast('已选内容没有可复制的文案');
      return;
    }
    const ok = await copyToClipboard(text);
    if (ok) {
      // 与 PostDetail 的成功反馈同语义:两短一短的震动节奏。
      buzz([10, 30, 10]);
      showBatchToast(`已复制 ${list.length} 篇文案`);
    } else {
      // 失败必须给明确反馈,不能静默(否则用户以为按钮坏了)。
      buzz(60);
      showBatchToast('复制失败，请长按选中文案手动复制');
    }
  }, [selectedPostsInPickOrder, store.filters.version, showBatchToast]);

  const batchFavorite = React.useCallback(() => {
    const list = selectedPosts;
    if (!list.length) {
      showBatchToast('没有可操作的已选内容');
      return;
    }
    store.setFavorites(list.map(p => p.id), true);
    buzz(12);
    showBatchToast(`已收藏 ${list.length} 篇`);
    clearSelection();
  }, [selectedPosts, store.setFavorites, showBatchToast, clearSelection]);

  const batchUnfavorite = React.useCallback(() => {
    const list = selectedPosts;
    if (!list.length) {
      showBatchToast('没有可操作的已选内容');
      return;
    }
    store.setFavorites(list.map(p => p.id), false);
    buzz(12);
    showBatchToast(`已取消收藏 ${list.length} 篇`);
    clearSelection();
  }, [selectedPosts, store.setFavorites, showBatchToast, clearSelection]);

  // T-Cron-2026-10-26 03:30 早间 — 改进清单 #5「下载体验」补齐:
  // **批量下载封面**(老仓库 App.jsx 的 batchDownloadCovers,Vite 重写时丢了)。
  //
  // 🔴 为什么这是可达性缺口而不是新功能:
  //   选题看板的核心产出就是「封面 + 文案」。文案能一键批量复制(已有),
  //   但封面**没有批量出口** —— 用户选 8 篇就只能一张一张点开详情、
  //   逐个点「保存封面」,8 篇 = 8 次打开 + 8 次点击 + 8 次等下载。
  //   而批量复制刚把文案一步带走,封面还留在原地 ⇒ **产出链条断了一半**。
  //
  // 复用 CoverDownload.download 而不是自己写 fetch + a[download]:
  //   · 华为/鸿蒙 webview 会拦截 a[download] ⇒ CoverDownload 已做分流
  //   · svg 封面需要 canvas 栅格化 ⇒ CoverDownload.toJpegBlob 已处理
  //   · 触屏设备逐张 a[download] 会被系统静默拦截 ⇒ 触屏走「打开新标签
  //     兜底 + 提示长按保存」,桌面走直接下载。
  //
  // 逐张串行 + 250ms 间隔:移动浏览器会限制并发下载,并发 8 张会被
  // 静默丢掉一半(老仓库踩过这个坑,注释里写明了)。
  const [batchDownloading, setBatchDownloading] = React.useState(false);

  // T-Cron-2026-10-27 08:00 早间 — 改进清单 #5「进度条:下载封面进度」的
  // **批量缺口**。
  //
  // 🔴 为什么这是真缺口而不是"单张已有就够":
  //   PostDetail 里单张封面下载有 .dl-progress 进度条(带字节数),
  //   但批量下载 8 张时,操作条只把按钮文案改成「下载中…」,
  //   **完全不知道进行到第几张**。8 张串行、每张 250ms 间隔 + 网络等待,
  //   最坏要十几秒 —— 这段时间用户看到的是一个「下载中…」的按钮,
  //   不知道是卡住了还是在跑,于是会**重复点**。
  //   (按钮虽然 disabled,但用户看不到卡顿 ≠ 看到进度。)
  //
  // 所以这里补一个「N/M 张」计数 + 进度条,让长任务可见。
  // 口径:进度 = 已完成张数 / 总张数(按张计,不按字节 —— 批量场景里
  // 用户关心的是「还剩几张」,不是「这几百 KB」)。
  const [batchProgress, setBatchProgress] = React.useState(null);

  const batchDownloadCovers = React.useCallback(async () => {
    if (batchDownloading) return;
    const list = selectedPosts;
    if (!list.length) {
      showBatchToast('没有可下载的已选内容');
      return;
    }

    // 封面解析口径必须与 PostCard / PostDetail 一致:
    //   版本级 coverUrl 优先(不同版本封面不同)⇒ 退回选题级 coverUrl。
    // 🔴 不要用 coverImageFile(那是文件名,不是可访问 URL)——
    //   老仓库用它当 URL 拼过,结果全是 404。
    const resolveCover = (p) => {
      const versions = (p && p.versions) || {};
      const keys = Object.keys(versions).sort();
      for (const ver of keys) {
        const u = versions[ver] && versions[ver].coverUrl;
        if (u) return u;
      }
      return (p && p.coverUrl) || '';
    };

    const items = list
      .map(p => ({ post: p, url: resolveCover(p) }))
      .filter(x => !!x.url);

    if (!items.length) {
      showBatchToast('已选内容均无封面');
      return;
    }

    const touch = typeof window.matchMedia === 'function'
      && window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    buzz(12);
    setBatchDownloading(true);
    setBatchProgress({ done: 0, total: items.length, failed: 0 });
    showBatchToast(`开始下载 ${items.length} 张封面…`);

    let done = 0;
    let failed = 0;
    for (const { post: p, url } of items) {
      const fileName = `${(p.book || '封面').replace(/[\\/:*?"<>|]/g, '')}-封面.jpg`;
      try {
        if (touch) {
          // 触屏:a[download] 常被静默拦截 ⇒ 打开新标签让用户长按保存。
          // 🔴 T136(2026-11-03 02:00):window.open 在微信 / FB·IG 内置浏览器里
          //   被弹窗拦截 ⇒ **返回 null 且不抛异常** ⇒ 旧代码照样 done += 1
          //   ⇒ toast 谎报「已打开 8 张封面」,实际一个标签页都没开。
          //   (与 references/web-share-fallback-chain.md §Pitfalls #6 一致)
          //   ⇒ 必须查返回值,拦截的这一张要计入 failed。
          const w = window.open(url, '_blank', 'noopener');
          if (!w) {
            failed += 1;
            setBatchProgress({ done: done + failed, total: items.length, failed });
            await new Promise(r => setTimeout(r, 250));
            continue;
          }
        } else {
          await CoverDownload.download(url, fileName);
        }
        done += 1;
      } catch (_) {
        // 单张失败不中断整批 —— 其余的照样能下。
        // 🔴 T114:失败要**计数**。以前失败被完全吞掉,
        // 8 张里挂了 3 张,toast 依然显示「已下载 5 张封面 ✓」,
        // 用户以为剩下 3 张也下好了,实际那 3 个根本没拿到文件。
        // 失败最常见的原因就是封面返回 SPA 回退 HTML(T114 已在
        // CoverDownload 里加了 magic bytes 校验,这里能拿到干净的失败信号)。
        failed += 1;
      }
      // 🔴 T116:每张完成后推进进度条 —— 批量是十几秒级的长任务,
      //   没有进度用户会以为卡死并重复点。
      setBatchProgress({ done: done + failed, total: items.length, failed });
      // 250ms 间隔:移动浏览器限制并发下载,并发会被静默丢弃。
      await new Promise(r => setTimeout(r, 250));
    }

    setBatchDownloading(false);
    setBatchProgress(null);
    if (done > 0 && failed === 0) {
      showBatchToast(touch
        ? `已打开 ${done} 张封面，可长按保存`
        : `已下载 ${done} 张封面 ✓`);
    } else if (done > 0) {
      // 部分成功:必须明确说清「有几张没下下来」,否则用户以为全好了。
      showBatchToast(touch
        ? `已打开 ${done} 张封面，${failed} 张打不开`
        : `已下载 ${done} 张封面，${failed} 张失败`);
    } else {
      showBatchToast('封面下载失败，请长按封面图保存');
    }
  }, [selectedPosts, batchDownloading, showBatchToast]);

  useUrlParams({
    onFilterToday: jumpToToday,
    onFilterFavorites: showFavoritesOnly,
    onSearch: focusSearch,
    onExport: () => exportVisible('csv'),
  });

  // ⚠️ T-Cron-2026-10-25 08:00:本 hook 调用**被下移到本文件更靠后的位置**。
  // 原因:本轮新增的 4 个快捷键 handler(jumpToLatest / resetFilters /
  // toggleSelectAll / handleBackToTop)定义在下方,而 hook 的参数对象
  // 会**立即求值** —— 如果 hook 调用在 handler 定义之前,
  // 那些 const 还在 TDZ(暂时性死区)⇒ 渲染时直接抛
  // `ReferenceError: Cannot access 'X' before initialization`
  // ⇒ 整站白屏。
  // ⇒ 铁律:**hook 的参数对象里引用的任何 const,必须在 hook 调用之前定义。**

  // Simulated initial load (skeleton screen)
  React.useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  // T-Cron-2026-10-25 08:00 早间 — 回到顶部(改进清单 #4「快捷键 t」+ #6「微动效」)
  //
  // 🔴 本轮修掉一个**线上真实存在的 P1 死功能**(不是理论问题,已实测):
  //   .app 是 `height:100vh` 的 grid,`.app-main` 是 `overflow-y:auto` 的滚动容器,
  //   而 **window 永远不滚动** —— 实测 documentScrollHeight(577) === innerHeight(577)。
  //   ⇒ 老代码 `window.addEventListener('scroll')` 永远不会触发
  //   ⇒ `showBackToTop` 永远是 false
  //   ⇒ 右下角「回到顶部」FAB 的 opacity 永远是 0 / pointer-events:none
  //   ⇒ **这个按钮从来就没显示过**,而且 `window.scrollTo` 也滚不动 .app-main。
  //
  // 实测证据(线上 canonical):
  //   appMainScrollHeight=322192  appMainClientHeight=473  (真正有 321k 内容)
  //   documentScrollHeight=577     windowInnerHeight=577     (window 压根不滚)
  //   手动把 .app-main.scrollTop 设成 5000 之后,FAB 仍是 opacity:0。
  //
  // 修法:监听**真正的滚动容器** .app-main,滚动也作用在它身上。
  //   · 用 ref 拿容器(而不是 querySelector)—— 组件渲染顺序保证它已挂载;
  //   · 监听器挂在容器上(不是 window),并且带 passive;
  //   · 找不到容器时**回退到 window**(防御:万一布局变了)。
  const mainScrollRef = React.useRef(null);

  // Back to top visibility —— 监听真正的滚动容器 .app-main。
  React.useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      setShowBackToTop(el.scrollTop > 400);
    };
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const handleBackToTop = () => {
    const el = mainScrollRef.current;
    if (el) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleTabChange = (tab) => {
    setMobileTab(tab);
    if (tab === 'favorites') {
      store.setFilter('onlyFavorites', true);
    } else {
      store.setFilter('onlyFavorites', false);
    }
  };

  const handleClearFilters = () => {
    store.clearFilters();
    setMobileTab('home');
  };

  // ────────────────────────────────────────────────────────────────────
  // T-Cron-2026-10-25 08:00 早间 — 改进清单 #4「快捷键」补齐
  //
  // 背景(diagnosis,不是猜测):清单里 #4 一直写着「✅ 完成」,但**线上生产树**
  // 的快捷键只有 8 个:`/ 1 2 e m c f ?`。老仓库(根目录 fork)里还有 4 个:
  //   t        回到顶部
  //   Shift+F  跳到最新批次
  //   Shift+R  重置全部筛选
  //   a        多选模式下全选 / 取消全选
  // ⇒ 「清单说完成」≠「生产上线了」(与 T104 的 #7 同一个陷阱)。
  //
  // 这 4 个键的 handler 在 App.jsx 里**全部已经存在**(jumpToToday /
  // showFavoritesOnly / handleClearFilters / setSelectedIds),
  // 所以本轮**只加键盘接线,不加业务逻辑** —— 零新行为、零新风险。
  // ────────────────────────────────────────────────────────────────────

  // Shift+F = 跳到最新批次(复用 jumpToToday,同一个筛选路径)。
  const jumpToLatest = React.useCallback(() => {
    jumpToToday();
  }, [jumpToToday]);

  // Shift+R = 重置全部筛选(复用 handleClearFilters,含移动端 tab 复位)。
  const resetFilters = React.useCallback(() => {
    handleClearFilters();
    showExportToast('已重置全部筛选');
  }, [handleClearFilters, showExportToast]);

  // a = 多选模式下全选 / 取消全选(与老仓库 `a` 同约定)。
  //
  // ⚠️ 语义细节:「全选」= 选中**当前可见**的卡片(不是全部 459 篇)——
  // 因为批量复制/收藏取的是 selectedPosts,而它按 store.filtered 过滤。
  // 如果这里选中了不可见的卡片,用户会看到「已选 200」但操作只对可见的 20 篇
  // 生效 ⇒ 计数与实际不符。所以必须按 filtered 取。
  //
  // 「取消全选」= 只清掉可见集合里的选中项(保留不可见项的选中状态),
  // 否则用户筛掉一半再按 a 会把另一半的选择也清掉,反直觉。
  const toggleSelectAll = React.useCallback(() => {
    const visible = store.filtered || [];
    if (!visible.length) return;
    const visibleIds = visible.map(p => p.id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = visibleIds.every(id => next.has(id));
      visibleIds.forEach(id => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  }, [store.filtered]);

  // T-Cron-2026-10-31 02:00 早间 (T127) — 改进清单 #8「批量操作」补全。
  //
  // BatchActionBar 的「全选 / 取消全选」按钮需要知道两件事:
  //   · visibleCount —— 当前筛选后可见的篇数(与 toggleSelectAll 口径一致)
  //   · allSelected  —— 可见集合是否**已经全部**被选中
  //
  // ⚠️ allSelected 必须按「可见集合」算,不能用 selectedIds.size ===
  //    count。因为用户可能筛掉一半再选中可见的那一半,
  //    此时 count>0 但可见集合并未全选 ⇒ 按钮应显示「全选」而不是
  //    「取消全选」,否则点了会反而把已选的取消掉(反直觉)。
  //
  // ⚠️ 短路顺序:visibleCount===0 时 allSelected 必须是 false,
  //    否则空列表会被算成「全选中」⇒ 按钮变成「取消全选」但什么也取消不了。
  const visibleSelectState = React.useMemo(() => {
    const visible = store.filtered || [];
    const visibleCount = visible.length;
    if (!visibleCount) return { visibleCount: 0, allSelected: false };
    const allSelected = visible.every(p => selectedIds.has(p.id));
    return { visibleCount, allSelected };
  }, [store.filtered, selectedIds]);

  // 全局快捷键接线 —— **必须放在上面所有 handler 定义之后**(见上方 TDZ 注释)。
  //
  // 本轮新增的 4 个键(改进清单 #4「快捷键」):
  //   t        → handleBackToTop  回到顶部(与右下角 FAB 等效)
  //   Shift+F  → jumpToLatest    跳到最新批次
  //   Shift+R  → resetFilters    重置全部筛选
  //   a        → toggleSelectAll 多选模式全选 / 取消全选
  useKeyboardShortcuts({
    onSearch: focusSearch,
    onViewGrid: () => setViewMode('grid'),
    onViewList: () => setViewMode('list'),
    onExport: () => exportVisible('csv'),
    onToggleSelectMode: toggleSelectMode,
    onCopy: copyDetailText,
    onCopyAllVersions: copyDetailAllVersions,
    onFav: toggleDetailFav,
    onHelp: () => setHelpOpen(v => !v),
    helpOpen,
    isDetailOpen: () => !!store.detailId,
    // ── 本轮新增 ──
    onBackToTop: handleBackToTop,
    onJumpLatest: jumpToLatest,
    onResetFilters: resetFilters,
    onSelectAll: toggleSelectAll,
    // `a` 只在多选模式下生效 —— 正常浏览时误按 'a' 把 459 张全选中是灾难。
    isSelectMode: () => selectMode,
    // 🔴 T136 (2026-11-03 02:00):模态弹层打开时,全局单键让位给模态。
    //
    // `aria-modal="true"` 只约束读屏和 Tab 焦点,不约束键盘事件 ⇒
    // 帮助面板开着按 2 会切背景视图;抽屉开着按 m 会让背景进入多选态。
    //
    // 🔴 T137 (2026-11-03 08:00 早间):补上 **MobileFilterDrawer**。
    //
    // ⚠️ T136 当时的注释写「MobileFilterDrawer 不在守卫里,挡掉背景单键
    //   反而更安全」—— 那句话把两件事混成了一件:
    //     (a) 抽屉**内部**要能继续点筛选项 → 由 isEditableTarget / 组件自身处理
    //     (b) 抽屉打开时**背景**的单键要能触发 → 这是要挡掉的
    //   (a) 和 (b) 互不冲突。抽屉自带 aria-modal="true"
    //   (MobileFilterDrawer.jsx:118),语义上就是模态 ⇒ (b) 必须挡。
    //
    // 【实测症状】移动端抽屉开着按 m ⇒ 背景进多选态,BatchActionBar(z=60)
    //   出现在抽屉(z=101)**背后** ⇒ 100% 点不到(T136 修掉的同一族假入口);
    //   按 e ⇒ 抽屉开着仍导出 CSV;按 t ⇒ 背景滚回顶部,筛选位置丢失。
    isModalOpen: () => helpOpen || !!store.detailId || filterOpen || exportMenuOpen,
    // 守卫需要区分「哪种模态」⇒ 把筛选抽屉的开关状态一并交给引擎。
    // 引擎侧按 helpOpen / filterOpen / 详情抽屉三档分别放行,
    // 不能笼统地「有模态就全挡」(那会把抽屉里的 c/f 打死)。
    filterOpen,
    // 🔴 T138 (2026-11-03 03:30 早间):第 4 档 —— 导出菜单。
    //
    // 【它不是 aria-modal,但是模态】role="menu" + aria-haspopup="menu" 的
    //   弹出菜单在语义上就是「模态于背景」:焦点被锁在菜单项之间(↑/↓ 导航),
    //   Esc 关闭并归还焦点。⇒ 背景的单键必须让位。
    //
    // 【实测症状(pre-fix,本地探针)】
    //   菜单开着按 `2` → 背景 .post-grid class 从 'post-grid ' 变成
    //                    'post-grid list-mode'(背景视图被切)
    //   菜单开着按 `e` → downloads=2(**导出两次**:菜单 item 一次 + 全局 e 一次)
    //   菜单开着按 `m` → batch_bar=True(背景进入多选态)
    //
    // 【放行哪些】↑/↓/Enter/Space/Esc 由菜单自身的 onKeyDown 处理
    //   (React 合成事件,在 root 上冒泡)⇒ 这里**必须挡住**全局同键,
    //   否则 Enter 会「菜单导出一次 + 全局 e 导出一次」。
    //   Esc 由 Header 的 document 级 listener 处理(它会 stopPropagation),
    //   但本层先 return 也不影响 —— Header 那个 listener 在 document 上,
    //   与本引擎的 document listener 同级,靠注册顺序;保险起见 Esc 放行。
    exportMenuOpen,
  });

  // T-Cron-2026-10-24 14:00 (round-2-of-day) — 改进清单 #9「统计:view count」。
  //
  // 「已读」总览 = 本地记录里**出现过**的篇数(去重后的 id 数),
  // 而不是浏览次数总和。
  //
  // 为什么用「篇数」而不是「次数」:
  //   Header 上的位置有限,而"我读过多少篇"是复盘时真正想知道的
  //   ("这批 12 篇我读了几篇?")。"次数总和"在频繁回看时会涨到两位数,
  //   反而看不出进度感。单篇的次数放在 PostDetail 里单独显示。
  //
  // 用 useMemo 依赖 store.viewCounts —— 浏览次数变化时才重算。
  const viewedCount = React.useMemo(() => {
    const counts = store.viewCounts || {};
    return Object.keys(counts).length;
  }, [store.viewCounts]);

  // 🔴 T-Cron-2026-10-31 14:00 (T125) — 改进清单 #10「无障碍」第 2 项
  // ──────────────────────────────────────────────────────────────────────
  // 【缺口】筛选/搜索的结果数量**只存在于视觉上**。
  //   Header 里有个「显示 N」的胶囊(纯视觉),ContentGrid 里的 sr-only
  //   播报只讲**虚拟滚动**的窗口状态(「已加载 8 组,还有更多」),
  //   **完全没讲「筛选后剩几篇」**。
  //
  //   ⇒ 读屏用户输入「设计」后,页面内容在变,但读屏**一声不吭**。
  //     他们不知道是命中了 3 篇、0 篇,还是搜索根本没生效 ——
  //     而「搜索有没有生效」恰恰是**最需要被播报**的一件事。
  //
  //   这与「刷新按钮无播报」(本轮第 1 项)是同一类病:
  //   **状态变化了,但变化的结果没有一条通道能到读屏用户耳朵里。**
  //
  // 【本轮的做法】在 App 根部挂一个 sr-only 的 live region,
  //   内容是「当前筛选条件下有 N 篇选题」。
  //
  //   口径选择(为什么只播报数量,不播报关键词):
  //   · 数量是**每次筛选都会变**的量 —— 正是 live region 该播报的东西;
  //   · 关键词本身在输入框里,读屏用户自己刚打完,再念一遍是噪音;
  //   · 播报完整句子(而不是裸数字)是因为读屏念裸数字很难懂上下文,
  //     「当前显示 12 篇选题」比「12」有用得多。
  //
  // 【为什么要防抖 —— 逐字播报会变成噪音】
  //   搜索框每敲一个字符就过滤一次。如果直接绑 stats.visible,
  //   敲「设计心理学」5 个字 = 读屏连播 5 句「当前显示 N 篇选题」,
  //   而且前 4 句都是中间态(往往都是 0 篇)—— 严重打断输入。
  //   ⇒ 用一个 400ms 的防抖:停手 400ms 后才播报最终结果。
  //     这与「用户在输入中途不该被打扰」的 AT 惯例一致。
  //
  // 【为什么 debounced 值不参与渲染其他部分】
  //   它只喂给 live region,不影响任何视觉输出 ⇒
  //   输入时组件不会因为它而重渲染(除了一次防抖后的 live region 更新)。
  const [liveVisible, setLiveVisible] = React.useState('');
  const visibleCount = store.stats.visible;
  // ⚠️ 字段名必须与 useContentStore 的 filters 形状**完全一致**:
  //    搜索字段叫 `search` 不是 `query`;另有 hotspot / version / category
  //    三个筛选维度。漏掉任何一个都会导致「改了那个筛选,数量变了,
  //    但 live region 没重新播报」—— 漏判的维度会让播报停留在旧数字。
  const filterSig = React.useMemo(() => {
    const f = store.filters || {};
    return [
      f.search || '', f.book || '', f.date || '', f.hotspot || '',
      f.tag || '', f.angle || '', f.source || '', f.version || '',
      f.category || '', f.batch || '', f.onlyFavorites ? '1' : '',
    ].join('|');
  }, [store.filters]);

  React.useEffect(() => {
    const t = setTimeout(() => {
      setLiveVisible(`当前显示 ${visibleCount} 篇选题`);
    }, 400);
    return () => clearTimeout(t);
  }, [visibleCount, filterSig]);

  return (
    <div className="app">
      {/* 读屏专用播报区:筛选/搜索结果数量变化时播报。
          视觉用户看的是 header 的「显示 N」胶囊,这里不重复视觉输出。 */}
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {liveVisible}
      </span>

      <Header
        stats={store.stats}
        onExport={exportVisible}
        onShowToday={jumpToToday}
        selectMode={selectionMode}
        onToggleSelectMode={toggleSelectMode}
        viewedCount={viewedCount}
        // 🔴 T148:清除本地阅读记录(隐私后路)。
        //   clearViewCounts() 此前是死导出 ⇒ 用户的浏览记录只能累积不能删除。
        //   入口挂在 Header 的「已读 N 篇」胶囊上(不新增常驻按钮,
        //   不占 header 空间 —— T144 的高度预算不受影响)。
        onClearViewCounts={store.clearAllViewCounts}
        onShowHelp={() => setHelpOpen(true)}
        onExportMenuOpenChange={setExportMenuOpen}
        // 🔴 T140:帮助入口的文案/图标必须跟面板内容一致
        //   (触屏面板是「操作说明」,入口也得这么说)。
        isTouchOnly={isTouchOnly}
      />

      <Sidebar
        filters={store.filters}
        bookList={store.bookList}
        dateList={store.dateList}
        hotspotList={store.hotspotList}
        tagList={store.tagList}
        angleList={store.angleList}
        sourceList={store.sourceList}
        batchList={store.batchList}
        onFilterChange={store.setFilter}
        onClear={store.clearFilters}
        searchInputRef={sidebarSearchRef}
        searchHistory={searchHistory.history}
        onCommitSearch={searchHistory.commit}
        onRemoveSearchHistory={searchHistory.remove}
        onClearSearchHistory={searchHistory.clear}
      />

      <main className="app-main" ref={mainScrollRef}>
        <ManualBanner url="https://goldwind.feishu.cn/docx/IpQ3dgkx3o2txMxxO0KcmOSSnmg" />

        {loading ? (
          <SkeletonGrid count={6} />
        ) : (
          <ContentGrid
            groups={store.groups}
            onToggleFav={store.toggleFavorite}
            onCardClick={store.openDetail}
            totalVisible={store.stats.visible}
            allPosts={store.posts}
            viewMode={viewMode}
            preferredVersion={store.filters.version}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            // T-Cron-2026-10-28 20:00 — 改进清单 #13「性能」。
            // .app-main 才是真正的滚动容器(不是 window)⇒ 虚拟滚动需要它来
            // ① 判定「触底」② 补偿卸载分组造成的滚动位移。
            scrollRootRef={mainScrollRef}
          />
        )}
      </main>

      <PostDetail
        post={store.detailPost}
        open={!!store.detailPost}
        onClose={store.closeDetail}
        onToggleFav={store.toggleFavorite}
        viewCount={store.detailId ? store.getViewCount(store.detailId) : 0}
        /* T-Cron-2026-11-01 14:00 — 修「详情抽屉版本与筛选不一致」(P1)。
           抽屉原来**完全收不到** preferredVersion ⇒ 永远从 A 版开始。
           ⇒ 用户筛「B版」→ 卡片显示 B → 点开详情却是 A ⇒
             「点开卡片看到的不是我筛的那个版本」。
           ⇒ 现在把全局版本筛选传进去,和 PostCard 用同一个
             resolveVersionKey 解析,两处口径一致。 */
        preferredVersion={store.filters.version}
      />

      <FavoriteBar
        count={store.stats.favorites}
        onClear={store.clearFavorites}
        favoritePosts={store.posts.filter((p) => p.favorite)}
      />

      {/* T-Cron-2026-10-24 02:00 早间:改进清单 #8「批量操作」操作条。
          T-Cron-2026-10-24 03:30:多选态(active)即显示 —— 桌面端点
          「多选」按钮进入时还没选卡片,操作条要给下一步提示。 */}
      <BatchActionBar
        count={selectedPosts.length}
        hiddenCount={hiddenSelectedCount}
        active={selectionMode}
        onCopy={batchCopy}
        onFavorite={batchFavorite}
        onUnfavorite={batchUnfavorite}
        onDownloadCovers={batchDownloadCovers}
        downloading={batchDownloading}
        progress={batchProgress}
        onCancel={clearSelection}
        /* T-Cron-2026-10-31 02:00 (T127):把「全选 / 取消全选」接到
           BatchActionBar —— 之前只有快捷键 a,鼠标/触屏用户没有等价入口。 */
        onSelectAll={toggleSelectAll}
        allSelected={visibleSelectState.allSelected}
        visibleCount={visibleSelectState.visibleCount}
      />

      <MobileBottomNav
        onOpenFilter={() => setFilterOpen(true)}
        activeTab={mobileTab}
        onTabChange={handleTabChange}
        favCount={store.stats.favorites}
        filterOpen={filterOpen}
      />

      <MobileFilterDrawer
        open={filterOpen}
        filters={store.filters}
        bookList={store.bookList}
        dateList={store.dateList}
        hotspotList={store.hotspotList}
        categoryList={store.categoryList}
        batchList={store.batchList}
        tagList={store.tagList}
        onClose={() => setFilterOpen(false)}
        onFilterChange={store.setFilter}
        onClear={handleClearFilters}
        searchInputRef={mobileSearchRef}
        searchHistory={searchHistory.history}
        onCommitSearch={searchHistory.commit}
        onRemoveSearchHistory={searchHistory.remove}
        onClearSearchHistory={searchHistory.clear}
      />

      <FloatingActionButton
        showBackToTop={showBackToTop}
        onBackToTop={handleBackToTop}
        viewMode={viewMode}
        onToggleView={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
        /* 🔴 T-Cron-2026-11-01 03:30 (T128):批量操作条可见时,FAB 让位。
           口径必须与 BatchActionBar 的渲染条件**完全一致** ——
           它的第一行是 `if (!count && !active) return null;`
           ⇒ 「有选中」或「多选态」任一成立,操作条就在屏幕上。
           这里若写成 `selectedIds.size > 0`,就会出现
           「刚点开多选、还没选卡片」时 FAB 仍然压着操作条的情况
           (操作条此时虽然空,但仍然占位、仍然挡)。 */
        batchBarActive={selectionMode || selectedIds.size > 0}
      />

      {/* T-Cron-2026-10-22 14:00:快捷键帮助面板。
          之前 Vite 树里**没有任何地方告诉用户快捷键 exists** ——
          老仓库有 SHORTCUT_GROUPS 面板,迁到 Vite 后丢失了。
          没有说明书 = 快捷键等于不存在,所以这一条必须和快捷键本身一起上线。 */}
      {helpOpen && (
        <>
          <div className="shortcut-help-overlay" onClick={() => setHelpOpen(false)} />
          <div
            ref={helpPanelRef}
            className="shortcut-help-panel"
            role="dialog"
            aria-modal="true"
            aria-label={isTouchOnly ? '操作说明' : '键盘快捷键'}
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
            // 🔴 T138:告诉 CSS(和探针)当前渲染的是哪一套内容。
            //   data-variant="touch" ⇒ 触屏操作清单
            //   data-variant="keyboard"(或不写)⇒ 物理键位清单
            data-variant={isTouchOnly ? 'touch' : 'keyboard'}
          >
            <div className="shortcut-help-header">
              {/* 🔴 T138:触屏用户看到的标题不能是「键盘快捷键」——
                  他们没有键盘。改成「操作说明」并给副标题。 */}
              <span className="shortcut-help-title">
                {isTouchOnly ? '操作说明' : '键盘快捷键'}
              </span>
              <button
                className="shortcut-help-close"
                onClick={() => setHelpOpen(false)}
                aria-label="关闭快捷键帮助"
              >
                ✕
              </button>
            </div>
            {isTouchOnly ? (
              /* 🔴 T138 触屏清单 —— 只列「真的能用手点出来」的动作 + 位置。
                 物理键位在触屏上一条都用不了 ⇒ 整块换成触屏清单。 */
              <ul className="shortcut-help-list shortcut-help-touch-list">
                {TOUCH_ACTIONS.map((a, i) => (
                  <li key={i} className="shortcut-help-touch-item">
                    <span className="shortcut-help-touch-icon" aria-hidden="true">{a.icon}</span>
                    <span className="shortcut-help-touch-body">
                      <span className="shortcut-help-touch-name">{a.name}</span>
                      <span className="shortcut-help-touch-where">{a.where}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="shortcut-help-list">
                {SHORTCUT_GROUPS.map((g, i) => (
                  <li key={i} className="shortcut-help-item">
                    <span className="shortcut-help-keys">
                      {g.keys.map((k, j) => (
                        <kbd key={j} className="shortcut-help-kbd">{k}</kbd>
                      ))}
                    </span>
                    <span className="shortcut-help-desc">{g.desc}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="shortcut-help-foot">
              {isTouchOnly ? '点右上角 ✕ 关闭' : '按 ? 或 Esc 关闭'}
            </div>
          </div>
        </>
      )}

      {/* T-Cron-2026-10-23 03:30:导出结果 toast。
          用 role="status" + aria-live="polite" —— 导出是异步的(生成 blob →
          触发下载),读屏用户需要听到"已导出 N 条 CSV"才知道成功了。
          T-Cron-2026-10-31:换成共享 DismissibleToast —— 支持点击 / Esc 关闭。 */}
      <DismissibleToast
        message={exportToast}
        onDismiss={() => {
          setExportToast('');
          if (exportToastTimer.current) clearTimeout(exportToastTimer.current);
        }}
      />
    </div>
  );
}

window.App = App;

export default App;
